import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import type { Editor } from '@tiptap/react';
import type { Node } from '@tiptap/pm/model';
import { GripVertical, Plus } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import {
  getSlashItems,
  position,
  SlashMenuPopup,
  type SlashItem,
} from './slash-menu.tsx';
import type { RichTextEditorLabels } from './labels.ts';

interface HoveredBlock {
  node: Node | null;
  pos: number;
}

export interface BlockHandleProps {
  editor: Editor;
  labels: RichTextEditorLabels;
}

/**
 * What the block under the pointer offers: add one after it, or move it.
 *
 * The plus is the slash menu without the keyboard — the same items, in the same
 * popup, for everyone who never finds out that "/" does anything. Focus stays in
 * the document the whole time, so whatever is picked can be typed into at once.
 */
export function BlockHandle({ editor, labels }: BlockHandleProps) {
  const hovered = useRef<HoveredBlock>({ node: null, pos: -1 });
  const plus = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  /*
   * The handle disappears the moment the pointer leaves the block, which would
   * take the open menu's trigger with it. Locking freezes it on the block it was
   * opened for — including which block `hovered` still points at.
   */
  const toggle = useCallback(
    (next: boolean) => {
      setOpen(next);
      editor.commands.setMeta('lockDragHandle', next);
    },
    [editor],
  );

  /* Stable, or the drag handle tears its ProseMirror plugin down on every render. */
  const onNodeChange = useCallback(
    ({ node, pos }: { node: Node | null; pos: number }) => {
      hovered.current = { node, pos };
    },
    [],
  );

  useLayoutEffect(() => {
    if (!open || !popup.current) return;
    position(popup.current, () => plus.current?.getBoundingClientRect() ?? null);
  }, [open]);

  /* Anything outside the menu closes it, the way the "/" menu answers Escape. */
  useEffect(() => {
    if (!open) return;
    /*
     * Capture, and kept to itself: the document answers Escape by blurring, and
     * on the way up it would take the caret with it. With the menu open, Escape
     * only closes the menu.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      toggle(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as globalThis.Node;
      if (popup.current?.contains(target) || plus.current?.contains(target)) return;
      toggle(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, toggle]);

  /*
   * An empty block to fill: the hovered one if it is still empty, a fresh one
   * right after it otherwise. Clicking + on a blank line should use that line
   * rather than leave it behind. Answers with the position inside it.
   */
  const emptyBlock = (): number => {
    const { node, pos } = hovered.current;
    if (!node || pos < 0) return editor.state.selection.to;
    if (node.type.name === 'paragraph' && node.content.size === 0) return pos + 1;
    const after = pos + node.nodeSize;
    editor.chain().insertContentAt(after, { type: 'paragraph' }).run();
    return after + 1;
  };

  const pick = (item: SlashItem) => {
    toggle(false);
    const at = emptyBlock();
    editor.chain().focus(at).run();
    /* An item deletes the "/query" that summoned it; summoned from here there is none. */
    item.run(editor, { from: at, to: at });
  };

  return (
    <DragHandle editor={editor} onNodeChange={onNodeChange}>
      <div className='flex items-center gap-0.5'>
        <IconButton
          ref={plus}
          size='sm'
          shape='square'
          aria-label={labels.addBlock}
          aria-haspopup='menu'
          aria-expanded={open}
          /*
           * Same 24px box as the grip beside it: the two read as one control,
           * and the handle keeps its top edge on the block's first line rather
           * than hanging below it.
           */
          className='size-6 rounded-sm text-ink-muted hover:text-ink'
          /* The handle around it is a drag source; this button is not. */
          draggable={false}
          /* Mousedown would move focus out of the editor before the click lands. */
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => toggle(!open)}
        >
          <Plus className='size-4' aria-hidden='true' />
        </IconButton>
        <span
          aria-hidden='true'
          className={cn(
            'flex size-6 cursor-grab items-center justify-center rounded-sm text-ink-muted',
            'transition-colors hover:bg-fill hover:text-ink active:cursor-grabbing',
          )}
        >
          <GripVertical className='size-4' />
        </span>
      </div>
      {open &&
        createPortal(
          <div ref={popup} className='z-50'>
            <SlashMenuPopup
              items={getSlashItems(editor)}
              selected={-1}
              noResults={labels.noResults}
              onPick={pick}
              onHover={() => {}}
            />
          </div>,
          document.body,
        )}
    </DragHandle>
  );
}
